import { useState } from 'react'
import { Trans } from 'next-i18next'
import {
  Box,
  Dialog,
  IllustrationSuccess,
  Inline,
  Text,
  TextLink,
  TextLinkButton
} from '@machobear/design-system'
import noop from 'lodash/noop'

import { isSocialRegistration } from '../utils'
import { LoginProvider, useLogin } from './context'
import ExistingEmailForDS from './existing-email-for-ds'
import ExistingSocialUserForDS from './existing-social-user-for-ds'
import ForgotPasswordForDS from './forgot-password-for-ds'
import LoginSocialForDS from './login-social-for-ds'
import RegisterForDS from './register-for-ds'
import RegisterTenantForDS from './register-tenant-for-ds'
import ResetPasswordSuccessForDS from './reset-password-success-for-ds'
import * as dsStyles from './styles.css'
import { DSAuthModalProps, DSLoginModalStep, DSStepProps } from './types'
import { ReactComponent as ExistingEmailIcon } from './images/existing-email.svg'

const AuthModal: React.FC<DSAuthModalProps> = ({
  mode = 'login',
  title,
  description,
  afterReset = noop,
  afterLogin = noop,
  data = {},
  eventId = 'web-signup-submit-default',
  defaultOperationType,
  listingId,
  listing,
  visible,
  onClose,
  t,
  autoFocusName = true
}) => {
  const [currentStep, setCurrentStep] = useState(mode)
  const [customTitle, setCustomTitle] = useState(title)
  const [customDescription, setCustomDescription] = useState(description)

  const { social, setEventId, setUsernameClicked, setUsername } = useLogin()

  const handleSetCurrentStep = (step: DSLoginModalStep) => {
    setCustomTitle('')
    setCustomDescription('')
    setCurrentStep(step)
  }

  const isSocial = isSocialRegistration(social)
  const socialPlatform = social?.platform

  const getTitleForRegister = () => {
    if (isSocial) {
      const socialPlatformName = t(
        `b.auth.view.social_platform_${socialPlatform}.text`,
        socialPlatform
      )
      return t('b.landing.view.create_an_account_social.title', {
        defaultValue: 'Sign up using your {{ socialPlatformName }} account',
        socialPlatformName
      })
    }
    return t('b.landing.view.create_an_account.title', 'Create an account')
  }
  const registerRoute = 'register'
  const redirectEventId =
    eventId === 'web-signup-submit-default'
      ? 'web-signup-submit-login-redirect'
      : eventId

  const renderLoginDescription = () => {
    return (
      <Inline space="xxxsmall" alignY="center">
        <Text size="small">
          {t(
            'b.auth.create.dont_have_an_account_alt.text',
            'Don’t have an account? '
          )}{' '}
        </Text>
        <Trans i18nKey="b.auth.create.sign_up.text" t={t}>
          <Inline alignY="center" space="none">
            <Text tone="link" size="small">
              <TextLinkButton
                id="web-auth-switch-to-signup"
                onClick={() => {
                  setEventId(redirectEventId)
                  setCurrentStep(registerRoute)
                }}
              >
                Sign up here
              </TextLinkButton>
            </Text>
            <Text>.</Text>
          </Inline>
        </Trans>
      </Inline>
    )
  }

  const renderRegisterDescription = () => {
    return (
      !isSocial && (
        <Inline space="xxxsmall" alignY="center">
          <Text size="small">
            {t(
              'b.auth.create.already_have_an_account_alt.text',
              'Already have an account?'
            )}{' '}
          </Text>

          <Trans
            t={t}
            i18nKey="b.auth.create.already_have_an_account_login_alt.text"
          >
            <Inline alignY="center" space="none">
              <Text tone="link" size="small">
                <TextLinkButton
                  onClick={() => setCurrentStep('login')}
                  id="web-auth-switch-to-login"
                >
                  Log in here
                </TextLinkButton>
              </Text>
              <Text>.</Text>
            </Inline>
          </Trans>
        </Inline>
      )
    )
  }

  const renderExistingEmailDescription = () => {
    return (
      <Text tone="secondary" size="small">
        <Trans i18nKey="b.auth.view.existing_email.text" t={t}>
          If you already have a liv.rent account with this email, please log in
          instead. If you are still having trouble, please reach out to us at{' '}
          <TextLink
            href="mailto:support@liv.rent"
            rel="noopener noreferrer"
            target="_blank"
          >
            support@liv.rent
          </TextLink>
          .
        </Trans>
      </Text>
    )
  }

  const renderExistingSocialUserDescription = () => {
    return (
      <Text tone="secondary" size="small">
        {t(
          'b.auth.view.existing.user.description',
          'You haven’t enabled login by social accounts. Please sign in by email and navigate to your account settings to connect your social accounts.'
        )}
      </Text>
    )
  }

  const renderForgotPasswordDescription = () => {
    return (
      <Text tone="secondary" size="small">
        {t(
          'b.auth.view.enter_email_to_receive_instructions.text',
          'Enter your email below and we will email instructions for setting a new one.'
        )}
      </Text>
    )
  }

  const renderResetPasswordSuccessDescription = () => {
    return (
      <Text tone="secondary" size="small">
        {t(
          'b.auth.edit.reset_sent.text',
          'The password reset link will be sent to the email associated with your account. If you do not receive the reset link within 10-15 minutes, please try this process again.'
        )}
      </Text>
    )
  }

  const STEPS: Record<DSLoginModalStep, DSStepProps> = {
    login: {
      component: LoginSocialForDS,
      title: t(
        'b.landing.view.welcome_back_login.text',
        'Welcome back! Log in below.'
      ),
      description: renderLoginDescription(),
      width: 520
    },
    register: {
      component: RegisterForDS,
      title: getTitleForRegister(),
      description: renderRegisterDescription(),
      width: 520
    },
    registerTenant: {
      component: RegisterTenantForDS,
      title: getTitleForRegister(),
      description: renderRegisterDescription(),
      width: 520
    },
    existingEmail: {
      component: ExistingEmailForDS,
      title: t(
        'b.auth.view.existing_email.title',
        'This email is already registered.'
      ),
      description: renderExistingEmailDescription(),
      illustration: <ExistingEmailIcon />,
      width: 460
    },
    existingSocialUser: {
      component: ExistingSocialUserForDS,
      title: t(
        'b.auth.view.existing.user.title',
        'Connect Social Accounts in Your Settings'
      ),
      description: renderExistingSocialUserDescription(),
      width: 540
    },
    forgotPassword: {
      component: ForgotPasswordForDS,
      title: t('b.auth.view.forgot_password.title', 'Forgot your password?'),
      description: renderForgotPasswordDescription(),
      width: 520
    },
    resetPasswordSuccess: {
      component: ResetPasswordSuccessForDS,
      title: t('b.auth.edit.reset_sent.title', 'Password Reset Requested'),
      description: renderResetPasswordSuccessDescription(),
      illustration: <IllustrationSuccess />,
      width: 520
    }
  }

  const current = STEPS[currentStep]
  const Component: React.ComponentType<any> = current.component
  const dialogTitle = current.title
  const dialogDescription = current.description
  const illustration = current?.illustration

  const handleClose = () => {
    setCurrentStep(mode)
    setUsernameClicked(false)
    setUsername('')
    onClose()
  }

  return (
    <Dialog
      open={visible}
      onClose={handleClose}
      id={currentStep}
      illustration={illustration}
      width="content"
      title={dialogTitle}
      description={
        <Box className={dsStyles.contentWrapperStyle}>{dialogDescription}</Box>
      }
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        paddingBottom={{ mobile: 'xxxxlarge', tablet: 'none' }}
        className={dsStyles.contentWrapperStyle}
      >
        <Component
          defaultOperationType={defaultOperationType}
          afterReset={afterReset}
          afterLogin2={afterLogin}
          mode={mode}
          setCurrentStep={handleSetCurrentStep}
          data={data}
          listingId={listingId}
          title={customTitle}
          description={customDescription}
          t={t}
          listing={listing}
          onClose={handleClose}
          autoFocusName={autoFocusName}
        />
      </Box>
    </Dialog>
  )
}

function DSLoginModal(props: DSAuthModalProps) {
  const { eventId = 'web-signup-submit-default' } = props

  return (
    <LoginProvider initialEventId={eventId}>
      <AuthModal {...props} />
    </LoginProvider>
  )
}

export default DSLoginModal
